// Usage:
//
// import useValidate, { is } from 'hooks/usevalidations'
//
// const [validations, validate] = useValidate(project.details, {
//   name: [is.present],
//   terms: [is.set],
//   country: [is.set],
//   company: [is.present],
//   email: [is.present, is.email],
//   phone: [is.present, is.phone],
// })

import { useEffect, useState } from 'react'
import { parsePhoneNumber } from 'libphonenumber-js'

const validators = {
  set: (value) => !!value,
  email: (email) => /^[_a-z0-9-A-Z-æøåÆØÅ]+(\.[_a-z0-9-A-Z-æøåÆØÅ]+)*@[a-z0-9-A-Z-]+(\.[a-z0-9-A-Z-]+)*(\.[a-z]{2,10})$/.test(email), // RFC822
  phone: (phone) => {
    if (!phone || !phone.number || !phone.country) {
      return false
    }
    const phoneNumber = parsePhoneNumber(phone.number, phone.country.toUpperCase());
    return phoneNumber && phoneNumber.isValid();
  },
  present: (value) => {
    if (value && typeof value === 'object') {
      return Object.keys(value).length > 0
    }
    return !!value && value.trim() !== '';
  },
}

export const is = Object
  .entries(validators)
  .reduce((memo, [key, validator]) => ({
    ...memo,
    [key]: [key, validator]
  }), {})


export const useValidate = (data, validations, autoValidate = false) => {
  const [validationErrors, setValidationErrors] = useState({})

  const resolveValidationErrors = () => {
    let errors = {}

    Object.entries(validations).forEach(([key, validators]) => {
      validators.forEach(([type, callback]) => {
        if (!callback(data[key])) {
            if (!errors[key]) {
              errors[key] = []
            }

            errors[key].push(type)
        }
      })
    })

    return errors
  }

  const validate = (onlyForKeys = []) => {
    const errors = resolveValidationErrors(onlyForKeys)
    let nextValidationErrors = { ...errors }

    if (onlyForKeys.length) {
      nextValidationErrors = { ...validationErrors }
      onlyForKeys.forEach((key) => {
        if (errors[key]) {
          nextValidationErrors[key] = errors[key]
        } else {
          delete nextValidationErrors[key]
        }
      })
    }

    setValidationErrors(nextValidationErrors)
    return !Object.keys(nextValidationErrors).length
  }

  useEffect(() => {
    if (autoValidate) {
      validate()
    }
  }, [data, autoValidate])

  return [validationErrors, validate]
}

export default useValidate
